import React, { useEffect, useRef, useState } from 'react';
import { ConfigProvider, Modal } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faEye } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../../context/ThemeContext';
import Table from '../../../commonComponents/Table';
import TablePagination from '../../../commonComponents/Pagination';
import CustomInput from '../../../commonComponents/input';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import { useSoldOutQuery } from './hooks/useSoldOutQuery';
import { useMarkAvailable } from './hooks/useSoldOutMutations';
import { SoldOutItem, SoldOutProps } from './types';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, SEARCH_DEBOUNCE_MS } from './constants';
import {
  BadgeOverlay,
  DescriptionText,
  ItemEyeBadge,
  ItemNameCell,
  ItemNameText,
  ItemThumbnailWrap,
  NoImagePlaceholder,
  PageWrapper,
  PreviewBadgeOverlay,
  PreviewImageWrapper,
  PreviewImg,
  SoldOutSwitch,
  SoldOutSwitchWrap,
  StatusTag,
  TableWrapper,
  ThumbnailImg,
} from './styles';

// Must match the "Sold out" tab's id in Menu/Constant.js
const SOLD_OUT_TAB_ID = 4;

const SoldOut: React.FC<SoldOutProps> = ({ tabIndex }) => {
  const { isDarkMode } = useTheme();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [togglingId, setTogglingId] = useState<number | string | null>(null);
  const [previewItem, setPreviewItem] = useState<SoldOutItem | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isActiveTab = tabIndex === SOLD_OUT_TAB_ID;
  const { data, isLoading, isFetching, refetch } = useSoldOutQuery(
    debouncedSearch,
    currentPage,
    pageSize,
    isActiveTab
  );
  const { mutate: markAvailable } = useMarkAvailable();

  useEffect(() => {
    if (isActiveTab) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveTab]);

  const items: SoldOutItem[] = data?.items ?? [];
  const total = data?.total ?? 0;

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handlePageChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const columns: ColumnsType<SoldOutItem> = [
    {
      title: 'ITEM',
      key: 'item',
      render: (_, record) => (
        <ItemNameCell>
          <ItemThumbnailWrap>
            {record.picture ? (
              <>
                <ThumbnailImg src={record.picture} alt={record.item} />
                <ItemEyeBadge onClick={() => setPreviewItem(record)}>
                  <FontAwesomeIcon icon={faEye} fontSize={9} color="#fff" />
                </ItemEyeBadge>
              </>
            ) : (
              <NoImagePlaceholder>
                <FontAwesomeIcon icon={faImage} fontSize={18} />
              </NoImagePlaceholder>
            )}
            {record.badgeImage && <BadgeOverlay src={record.badgeImage} alt="badge" />}
          </ItemThumbnailWrap>
          <ItemNameText>{record.item}</ItemNameText>
        </ItemNameCell>
      ),
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'description',
      key: 'description',
      render: (desc: string) => <DescriptionText>{desc || 'No description available'}</DescriptionText>,
    },
    {
      title: 'PRICE',
      dataIndex: 'price',
      key: 'price',
      align: 'center',
      render: (price: number) => (
        <StatusTag $variant="price" $isDark={isDarkMode}>
          {price !== null && price !== undefined ? `$${(Number(price) || 0).toFixed(2)}` : 'Free'}
        </StatusTag>
      ),
    },
    {
      title: 'TAXABLE',
      dataIndex: 'taxable',
      key: 'taxable',
      align: 'center',
      render: (taxable: boolean) => (
        <StatusTag $variant={taxable ? 'yes-green' : 'no'} $isDark={isDarkMode}>
          {taxable ? 'Yes' : 'No'}
        </StatusTag>
      ),
    },
    {
      title: 'MEAL',
      dataIndex: 'is_make_it_a_meal',
      key: 'is_make_it_a_meal',
      align: 'center',
      render: (val: boolean) => (
        <StatusTag $variant={val ? 'yes-blue' : 'no'} $isDark={isDarkMode}>
          {val ? 'Yes' : 'No'}
        </StatusTag>
      ),
    },
    {
      title: 'SOLD OUT',
      key: 'sold_out',
      align: 'center',
      render: (_, record) => {
        const isThis = togglingId === record.id;
        const isBusy = togglingId !== null && !isThis;
        return (
          <SoldOutSwitchWrap $busy={isBusy}>
            <SoldOutSwitch
              $soldOut={record.sold_out}
              checked={record.sold_out}
              loading={isThis}
              disabled={isBusy}
              onChange={() => {
                setTogglingId(record.id);
                markAvailable(record.id, { onSettled: () => setTogglingId(null) });
              }}
              checkedChildren="Sold"
              unCheckedChildren="On"
            />
          </SoldOutSwitchWrap>
        );
      },
    },
  ];

  return (
    <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
      <PageWrapper>
        <CustomInput
          placeholder="Search items..."
          value={searchQuery}
          onChange={handleSearch}
          allowClear
          width="300px"
        />

        <TableWrapper>
          <Table<SoldOutItem>
            columns={columns}
            dataSource={items}
            rowKey="id"
            loading={isLoading || isFetching}
            pagination={false}
            scroll={items.length > 0 ? { x: true } : undefined}
            disableScroll={items.length === 0}
            emptyText="No sold out items found"
            headerBackgroundColor={isDarkMode ? '#1a202c' : '#F2F2F2'}
            removeVerticalBorders
            thPadding="15px 10px"
            tdPadding="10px 10px"
            firstChildBorderRadius="8px"
            border="0"
            borderBottomLeftRight
            theme={isDarkMode ? 'dark' : 'light'}
          />
        </TableWrapper>

        {total > 0 && (
          <TablePagination
            totalCount={total}
            currentPage={currentPage}
            pageSize={pageSize}
            onChange={handlePageChange}
            totalLabel="Total sold out items"
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            padding="16px 12px 0px 12px"
          />
        )}

        <Modal
          open={!!previewItem}
          onCancel={() => setPreviewItem(null)}
          centered
          width="auto"
          title={previewItem?.item}
          footer={null}
          styles={{ body: { padding: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' } }}
        >
          <PreviewImageWrapper>
            <PreviewImg src={previewItem?.picture || ''} alt={previewItem?.item} />
            {previewItem?.badgeImage && <PreviewBadgeOverlay src={previewItem.badgeImage} alt="badge" />}
          </PreviewImageWrapper>
        </Modal>
      </PageWrapper>
    </ConfigProvider>
  );
};

export default SoldOut;
